// The interactive cell types. Each reads a structured value from the row's field:
//   link    {label, href, external?, onSelect?, ariaLabel?}  a Buttons inline link; a plain string is text
//   button  {label, onSelect | href, icon?, disabled?, ariaLabel?}
//   split   {label, onSelect | href, actions: [item], disabled?, ariaLabel?, menuLabel?}  a Buttons split button
// and an actions column reads its row's items from column.actions(row). A menu item is a command or a
// destination ({key, label, onSelect | href, icon?, hint?, danger?, disabled?}). A destination that also
// declares onSelect is handled in place.

import React, {
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import PropTypes from 'prop-types'

import { Button, InlineLink, SplitButton } from '@CommonComponents/buttons'
import { TableContext } from '../context'
import { Tooltip } from '../tooltip/Tooltip'
import { splitMenuLabel } from './copy'
import {
  activate,
  blockedReason,
  blockedState,
  iconClass,
  isUsableAction,
  menuItem,
  menuWidth,
} from './menuItems'
import { RowActions } from './RowActions'

const isText = value => typeof value === 'string' && value.trim() !== ''
const CELL_BUTTON = 'me-btn me-btn-secondary me-btn-xs'

const structured = value =>
  value && typeof value === 'object' && !Array.isArray(value) ? value : null

// Discloses the complete value only while the wrapped content is visually clipped. The wrapped control
// stays the focus target, so no tab stop is added.
export const Clipped = ({ text, children }) => {
  const ref = useRef(null)
  const [clipped, setClipped] = useState(false)
  const measure = useCallback(() => {
    const node = ref.current
    if (!node) return
    // a link truncates on its own box (z6-me-table-cells.css), other content on this one
    const box =
      node.firstElementChild &&
      node.firstElementChild.classList.contains('me-btn-link-inline')
        ? node.firstElementChild
        : node
    setClipped(box.scrollWidth > box.clientWidth)
  }, [])
  useLayoutEffect(() => {
    measure()
  }, [text, measure])
  useEffect(() => {
    const node = ref.current
    if (!node) return undefined
    window.addEventListener('resize', measure)
    let observer = null
    if (typeof window.ResizeObserver === 'function') {
      observer = new window.ResizeObserver(measure)
      observer.observe(node)
    }
    return () => {
      window.removeEventListener('resize', measure)
      if (observer) observer.disconnect()
    }
  }, [measure])
  return (
    <span ref={ref} className="me-table-clipped">
      <Tooltip text={text} disabled={!clipped}>{children}</Tooltip>
    </span>
  )
}

Clipped.propTypes = {
  text: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
}

export const LinkCell = ({ column, row }) => {
  const raw = row[column.key]
  const value = structured(raw)
  if (!value) return raw === null || raw === undefined ? null : String(raw)
  if (!isText(value.label)) return null
  if (!isText(value.href)) return value.label
  const link = (
    <InlineLink
      href={value.href}
      external={Boolean(value.external)}
      aria-label={isText(value.ariaLabel) ? value.ariaLabel : undefined}
      onClick={activate(value)}
    >
      {value.label}
    </InlineLink>
  )
  return column.wrap === 'ellipsis' ? (
    <Clipped text={value.label}>{link}</Clipped>
  ) : (
    link
  )
}

const cellPropTypes = {
  column: PropTypes.object.isRequired,
  row: PropTypes.object.isRequired,
}

LinkCell.propTypes = cellPropTypes

// a cell's command conflicts with bulk work as a row action does: it stays in place, aria-disabled, with the reason
const useBlocked = () => {
  const table = useContext(TableContext)
  return action => blockedState(table && table.rowActionState, action)
}

const segment = (value, className, state, reasonId) => {
  const reason = blockedReason(state, reasonId)
  return (
    <Button
      className={className}
      href={isText(value.href) ? value.href : undefined}
      icon={iconClass(value.icon)}
      aria-label={isText(value.ariaLabel) ? value.ariaLabel : undefined}
      aria-disabled={value.disabled || state.blocked || undefined}
      aria-describedby={reason.describedBy}
      data-tooltip={state.blocked && state.reason ? state.reason : undefined}
      onClick={activate(value)}
    >
      {value.label}
      {reason.node}
    </Button>
  )
}

const isActionValue = value =>
  Boolean(value) &&
  isText(value.label) &&
  (typeof value.onSelect === 'function' || isText(value.href))

export const ButtonCell = ({ column, row }) => {
  const blocked = useBlocked()
  const reasonId = useId()
  const value = structured(row[column.key])
  return isActionValue(value)
    ? segment(value, CELL_BUTTON, blocked(value), reasonId)
    : null
}

ButtonCell.propTypes = cellPropTypes

export const SplitCell = ({ column, row }) => {
  const blocked = useBlocked()
  const reasonId = useId()
  const value = structured(row[column.key])
  if (!isActionValue(value)) return null
  const items = (value.actions || []).filter(isUsableAction)
  const state = blocked(value)
  const itemStates = items.map(item => blocked(item))
  // a blocked action leaves its menu open only while an item in it stays usable
  const usableItem = items.some(
    (item, index) => !item.disabled && !itemStates[index].blocked
  )
  const reason = blockedReason(state, reasonId)
  return (
    <SplitButton
      className={CELL_BUTTON}
      menuMinWidth={menuWidth(column.minMenuWidth)}
      menuLabel={
        isText(value.menuLabel) ? value.menuLabel : splitMenuLabel(value.label)
      }
      href={isText(value.href) ? value.href : undefined}
      icon={iconClass(value.icon)}
      aria-label={isText(value.ariaLabel) ? value.ariaLabel : undefined}
      aria-disabled={value.disabled || state.blocked || undefined}
      aria-describedby={reason.describedBy}
      data-tooltip={state.blocked && state.reason ? state.reason : undefined}
      menuDisabled={state.blocked && !value.disabled ? !usableItem : undefined}
      onClick={activate(value)}
      items={items.map((item, index) =>
        menuItem(item, index, itemStates[index], `${reasonId}-item`)
      )}
    >
      {value.label}
      {reason.node}
    </SplitButton>
  )
}

SplitCell.propTypes = cellPropTypes

export const ActionsCell = ({ column, row }) => (
  <RowActions
    actions={typeof column.actions === 'function' ? column.actions(row) : []}
    label={column.actionsLabel}
    minMenuWidth={column.minMenuWidth}
  />
)

ActionsCell.propTypes = cellPropTypes
